import { useEffect } from "react";
import { motion } from "framer-motion";
import type { AnswerResult, DailyItem, Game } from "@/lib/types";
import { GAME_LABEL, PERSON_LABEL, TENSE_LABEL } from "@/lib/types";
import { useSessionStore } from "@/state/session";
import { navigate } from "@/lib/router";
import { bigCelebration, sfxComplete } from "@/lib/rewards";

export function GameResults({ game }: { game: Game }) {
  const loaded = useSessionStore((s) => s.loaded);
  const reset = useSessionStore((s) => s.reset);

  useEffect(() => {
    if (!loaded) return;
    if (loaded.session.correct / Math.max(1, loaded.session.total) >= 0.8) {
      bigCelebration();
    }
    sfxComplete();
  }, [loaded]);

  if (!loaded) return null;
  const label = GAME_LABEL[game];
  const { correct, total, best_streak } = loaded.session;
  const accuracy = Math.round((correct / total) * 100);
  const missed = loaded.missed;
  const reward = loaded.reward;

  return (
    <div className="min-h-[100dvh] w-full max-w-lg mx-auto flex flex-col safe-pt safe-pb px-5">
      <div className="flex-1 py-6">
        <div className="w-full max-w-md mx-auto text-center">
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className="text-6xl mb-2"
          >
            {label.emoji}
          </motion.div>

          {reward && (
            <div className="mt-3 chunk p-4 bg-brand-saffron/15">
              <div className="font-display text-2xl">+{reward.xp_gain} XP</div>
              {reward.unlocked.length > 0 && (
                <div className="mt-1 text-sm font-bold text-brand-muted">
                  🏅 {reward.unlocked.length} logro nuevo
                </div>
              )}
            </div>
          )}
          <div className="font-display text-3xl">{encouragement(accuracy)}</div>
          <div className="mt-1 text-brand-muted font-bold uppercase tracking-widest text-xs">
            {label.title} · listo por hoy
          </div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-5 grid grid-cols-3 gap-3"
          >
            <Stat big>{correct}</Stat>
            <Stat label="de" big>
              {total}
            </Stat>
            <Stat label="mejor racha" big>
              {best_streak}🔥
            </Stat>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-3 chunk p-4 text-left"
          >
            <div className="text-xs text-brand-muted uppercase tracking-widest font-black">
              Precisión
            </div>
            <div className="font-display text-4xl">{accuracy}%</div>
            <div className="mt-2 h-2 rounded-full bg-brand-border overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${accuracy}%` }}
                transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
                className="h-full bg-brand-mint"
              />
            </div>
          </motion.div>

          {missed.length > 0 && (
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.45 }}
              className="mt-4 text-left"
            >
              <div className="flex items-baseline justify-between px-1 mb-2">
                <h2 className="font-display text-xl">Para repasar</h2>
                <span className="pill bg-brand-saffron/20 border-brand-saffron/40 text-brand-ink">
                  {missed.length} palabra{missed.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="space-y-2">
                {missed.map(({ item, result }, i) => (
                  <li key={item.id}>
                    <ReviewCard item={item} result={result} index={i} />
                  </li>
                ))}
              </ul>
            </motion.section>
          )}
        </div>
      </div>

      <button
        onClick={() => {
          reset();
          navigate({ name: "home" });
        }}
        className="chunk-btn-primary w-full mt-6 sticky bottom-4"
      >
        Volver al inicio
      </button>
    </div>
  );
}

function ReviewCard({
  item,
  result,
  index,
}: {
  item: DailyItem;
  result: AnswerResult;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 + index * 0.03 }}
      className="chunk p-3"
    >
      {item.kind === "el_la" && (
        <div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg">
              <span
                className={
                  item.correct_article === "el"
                    ? "text-brand-sky"
                    : "text-brand-rose"
                }
              >
                {item.correct_article}
              </span>{" "}
              {item.lemma}
            </span>
            <span className="text-xs text-brand-muted font-bold ml-auto">
              {item.english}
            </span>
          </div>
          {result.user_answer.trim() && (
            <div className="mt-0.5 text-xs text-brand-muted font-bold">
              Tú: <span className="line-through">{result.user_answer}</span>
            </div>
          )}
          {item.note && (
            <p className="mt-1.5 text-xs bg-brand-saffron/15 rounded-xl px-2 py-1 font-bold text-brand-ink">
              {item.note}
            </p>
          )}
        </div>
      )}

      {item.kind === "vocab" && (
        <div>
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-xs text-brand-muted uppercase tracking-widest font-black">
              {item.direction === "en2es" ? "EN → ES" : "ES → EN"}
            </div>
            <div className="text-[10px] text-brand-muted font-bold">
              {item.category}
            </div>
          </div>
          <div className="mt-1 font-display text-lg">{item.prompt}</div>
          <div className="mt-0.5 text-sm font-black text-brand-mint">
            {item.answers.slice(0, 3).join(" · ")}
          </div>
          {result.user_answer.trim() && (
            <div className="mt-0.5 text-xs text-brand-muted font-bold">
              Tú: <span className="line-through">{result.user_answer}</span>
            </div>
          )}
          {item.note && (
            <p className="mt-1.5 text-xs bg-brand-saffron/15 rounded-xl px-2 py-1 font-bold text-brand-ink">
              {item.note}
            </p>
          )}
        </div>
      )}

      {item.kind === "conj" && (
        <div>
          <div className="flex flex-wrap items-center gap-1">
            <span className="pill bg-brand-violet/10 border-brand-violet/30 text-brand-violet">
              {TENSE_LABEL[item.tense]}
            </span>
            <span className="pill">{PERSON_LABEL[item.person]}</span>
            <span className="pill bg-brand-saffron/15 border-brand-saffron/40 text-brand-ink">
              {item.infinitive}
            </span>
          </div>
          <div className="mt-1 font-display text-lg text-brand-mint">
            {item.correct_form}
          </div>
          {result.user_answer.trim() && (
            <div className="mt-0.5 text-xs text-brand-muted font-bold">
              Tú: <span className="line-through">{result.user_answer}</span>
            </div>
          )}
          {result.explanation && (
            <p className="mt-1.5 text-xs bg-brand-sky/10 rounded-xl px-2 py-1 font-bold text-brand-ink">
              {result.explanation}
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
}

function Stat({
  big = false,
  label,
  children,
}: {
  big?: boolean;
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="chunk p-3">
      <div
        className={`font-display leading-none ${big ? "text-3xl" : "text-xl"}`}
      >
        {children}
      </div>
      {label && (
        <div className="mt-1 text-[10px] text-brand-muted uppercase tracking-widest font-black">
          {label}
        </div>
      )}
    </div>
  );
}

function encouragement(accuracy: number): string {
  if (accuracy === 100) return "¡Perfecto!";
  if (accuracy >= 90) return "¡Impecable!";
  if (accuracy >= 75) return "¡Bien hecho!";
  if (accuracy >= 50) return "Buen intento";
  return "A seguir practicando";
}
